import WizardTextAreaComponent from 'components/v2/epd-wizard-inputs/WizardTextArea';
import { Column, ColumnEvent } from 'primereact/column';
import { Dropdown, DropdownChangeEvent, DropdownProps } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import {
  useCreateEpdScenarioModule,
  useCreateEpdScenarioModuleDefaultDescription,
  useDeleteEpdScenarioModules,
  useUpdateEpdScenarioModule,
  useUpdateEpdScenarioModuleDefaultDescription,
} from 'services/api/mutations';
import { useEpdScenarioModule } from 'services/api/queries';
import styled, { css } from 'styled-components';
import { Option, ScenarioModuleDefaultDescriptionModel, ScenarioModuleItemModel } from 'types/types';

import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import DynamicGridHeader from '../DynamicGridHeader';
import { cellTextEditor } from '../GridCellEditors';
import { DataTableStyled, Placeholder } from '../StyledGrid';
import useDynamicGridWithEmptyFirstRow from '../useDynamicGridWithEmptyFirstRow';
import useGridRowSelectionViaCheckbox from '../useGridRowSelectionViaCheckbox';

type TProps = {
  epdVersionId: string;
  scenarioId: string;
  module: string;
  commonModule?: string;
  moduleName: string;
  unitOptions: Option[] | undefined;
  moduleExplanatoryNameError?: any;
  moduleBriefDescriptionError?: any;
  moduleDescriptionError?: any;
  isDefaultScenario?: boolean;
};

const ModuleGrid = ({
  epdVersionId,
  scenarioId,
  module,
  commonModule,
  moduleName,
  unitOptions,
  moduleExplanatoryNameError,
  moduleBriefDescriptionError,
  moduleDescriptionError,
  isDefaultScenario,
}: TProps) => {
  const { t } = useTranslation();
  const isReadOnly = useIsReadOnlyMode();

  const scenarioModule = useEpdScenarioModule({ epdVersionId, scenarioId, module }).data;
  const createMutation = useCreateEpdScenarioModule(epdVersionId, scenarioId, module);
  const updateMutation = useUpdateEpdScenarioModule(epdVersionId, scenarioId, module);
  const deleteMutation = useDeleteEpdScenarioModules(epdVersionId, scenarioId, module);

  const { dataTableProps, checkboxColumnNode, handleDeleteSelected, isDeleteDisabled, gridId } =
    useGridRowSelectionViaCheckbox<ScenarioModuleItemModel>(isReadOnly, deleteMutation);

  const createDefaultDescriptionMutation = useCreateEpdScenarioModuleDefaultDescription(epdVersionId, scenarioId, module);
  const updateDefaultDescriptionMutation = useUpdateEpdScenarioModuleDefaultDescription(epdVersionId, scenarioId, module);

  const rowClassName = () => ({ 'p-disabled': isReadOnly });

  const { gridData, handleAddRow, saveModifiedGridData } = useDynamicGridWithEmptyFirstRow<ScenarioModuleItemModel>({
    rows: scenarioModule?.scenarioModuleItems,
    createMutation,
    updateMutation,
    emptyEntity: { scenarioId, module, description: '', value: '', unitId: undefined },
  });

  const handleDefaultDescriptionChange = (name: any, value: any) => {
    if (
      scenarioModule?.defaultScenarioModuleExplanatoryName === null &&
      scenarioModule?.defaultScenarioModuleBriefDescription === null &&
      scenarioModule?.defaultScenarioModuleDescription === null
    ) {
      createDefaultDescriptionMutation.mutate({
        epdVersionId,
        scenarioId,
        module,
        [name]: value,
      } as ScenarioModuleDefaultDescriptionModel);
      return;
    }

    updateDefaultDescriptionMutation.mutate({
      ...scenarioModule,
      epdVersionId,
      scenarioId,
      module,
      [name]: value,
    } as ScenarioModuleDefaultDescriptionModel);
  };

  const onCellEditComplete = (e: ColumnEvent) => {
    let { rowData, newValue, field } = e;
    const newData = { ...rowData, [field]: newValue };

    if (newData.mandatory && newData.description === newData.placeholder) {
      newData['description'] = t(
        `epdCompiler.step3.scenarioTab.epdScenarioModule.${module}.description.${rowData.placeholder}`
      );
    }

    saveModifiedGridData(newData);
  };

  const header = (
    <DynamicGridHeader
      caption="Scenario data"
      onAddRow={handleAddRow}
      onDeleteRows={handleDeleteSelected}
      deleteRowsDisabled={isDeleteDisabled}
    />
  );

  const descriptionBodyTemplate = (rowData: ScenarioModuleItemModel) => {
    return rowData?.mandatory ? (
      <DescriptionCell
        readonly={true}
        dangerouslySetInnerHTML={{
          __html: t(`epdCompiler.step3.scenarioTab.epdScenarioModule.${module}.description.${rowData.placeholder}`),
        }}
      />
    ) : (
      <DescriptionCell readonly={false}>
        {rowData?.placeholder && !rowData?.description ? (
          <Placeholder>
            {t(`epdCompiler.step3.scenarioTab.epdScenarioModule.${module}.description.${rowData.placeholder}`)}
          </Placeholder>
        ) : (
          rowData?.description ?? <Placeholder>Type here</Placeholder>
        )}
      </DescriptionCell>
    );
  };

  const valueBodyTemplate = (rowData: ScenarioModuleItemModel) => {
    return rowData?.value ? (
      rowData.value
    ) : rowData.placeholder ? (
      <Placeholder>
        {t(`epdCompiler.step3.scenarioTab.epdScenarioModule.${module}.value.${rowData.placeholder}`)}
      </Placeholder>
    ) : (
      <Placeholder>Type value as appropriate</Placeholder>
    );
  };

  const dropdownValueTemplate = (option: Option, props: DropdownProps) => {
    return option && option !== null ? (
      <div dangerouslySetInnerHTML={{ __html: option.label }} />
    ) : (
      <div>{props.placeholder}</div>
    );
  };

  const dropdownItemTemplate = (option: Option) => {
    return option && option !== null && <div dangerouslySetInnerHTML={{ __html: option.label }} />;
  };

  const unitBodyTemplate = (rowData: ScenarioModuleItemModel, dropdownOptions: Option[]) => {
    const kWhMJOption = dropdownOptions.find((d) => d.label === 'kWh/MJ');

    const options = [...dropdownOptions].filter(
      (option) => option.label !== 'kWh/MJ' || kWhMJOption?.value === rowData.unitId
    );

    return rowData && (rowData.unitRequired || !rowData.mandatory) ? (
      <Dropdown
        style={{ width: '100%', minWidth: '1rem', border: 'none', backgroundColor: 'transparent' }}
        placeholder="Select"
        value={rowData.unitId}
        valueTemplate={dropdownValueTemplate}
        itemTemplate={dropdownItemTemplate}
        optionLabel="label"
        optionValue="value"
        options={options}
        onChange={(e: DropdownChangeEvent) => {
          rowData.unitId = e.value;
          updateMutation.mutate(rowData);
        }}
        disabled={isReadOnly}
      />
    ) : (
      <CellContainer readonly={true}>&nbsp;</CellContainer>
    );
  };

  return (
    <>
      {isDefaultScenario && module !== 'RSL' && (
        <>
          <WizardTextAreaComponent
            label={`Explanatory name of the default scenario in module ${commonModule ?? module}`}
            name="defaultScenarioModuleExplanatoryName"
            value={scenarioModule?.defaultScenarioModuleExplanatoryName}
            onChanged={handleDefaultDescriptionChange}
            error={moduleExplanatoryNameError}
            maxLength={23}
            required
          />
          <WizardTextAreaComponent
            label={`Brief description (<86 characters) of the default scenario in module ${commonModule ?? module}`}
            name="defaultScenarioModuleBriefDescription"
            value={scenarioModule?.defaultScenarioModuleBriefDescription}
            onChanged={handleDefaultDescriptionChange}
            error={moduleBriefDescriptionError}
            maxLength={85}
            required
          />
        </>
      )}
      <WizardTextAreaComponent
        label={`Description of the ${isDefaultScenario ? 'default' : 'additional'} scenario in ${
          module === 'RSL' ? 'reference service life' : `module ${commonModule ?? module}`
        }`}
        name="defaultScenarioModuleDescription"
        value={scenarioModule?.defaultScenarioModuleDescription}
        onChanged={handleDefaultDescriptionChange}
        error={moduleDescriptionError}
        maxLength={7000}
        tooltip={
          isDefaultScenario
            ? 'The longer description from this field will be displayed in the PDF when provided. Otherwise, the brief description will be shown.'
            : undefined
        }
      />
      <ModuleDataTableStyled
        key={gridId}
        header={header}
        value={gridData}
        dataKey="ord"
        showGridlines
        editMode="cell"
        required={
          !scenarioModule?.defaultScenarioModuleDescription && scenarioModule?.scenarioModuleItems.every((x) => !x.value)
        }
        rowClassName={rowClassName}
        {...dataTableProps}
      >
        {checkboxColumnNode}
        <Column
          field="description"
          header={moduleName}
          body={descriptionBodyTemplate}
          editor={isReadOnly ? undefined : cellTextEditor}
          onCellEditComplete={onCellEditComplete}
          onBeforeCellEditShow={(event) => {
            if (event.rowData.mandatory) {
              event.originalEvent.preventDefault();
              return false;
            }
          }}
          style={{ width: '40%' }}
        />
        <Column
          field="value"
          header="Value"
          body={valueBodyTemplate}
          editor={isReadOnly ? undefined : cellTextEditor}
          onCellEditComplete={onCellEditComplete}
          className="padding"
          style={{ width: '40%' }}
        />
        <Column
          field="unitId"
          header="Unit"
          body={(rowData: ScenarioModuleItemModel) => unitBodyTemplate(rowData, unitOptions ?? [])}
        />
      </ModuleDataTableStyled>
    </>
  );
};

const CellContainer = styled.div<{ readonly: boolean }>`
  ${(props) =>
    props.readonly &&
    css`
      background-color: ${(props) => props.theme.colors.elementsFilledInput};
    `}
  padding: 0.5rem 1rem;
  height: 100%;
`;

const ModuleDataTableStyled = styled(DataTableStyled)`
  .p-datatable-table {
    height: 100%;
  }
  .p-datatable-tbody > tr > td {
    padding: 0;
  }
  .p-datatable-tbody > tr > td.padding,
  .p-datatable-tbody > tr > td.p-selection-column,
  .p-datatable-tbody > tr.p-datatable-emptymessage > td {
    padding: 0.5rem 1rem;
  }
  && .p-inputtext.p-dropdown-label.p-placeholder {
    color: ${(props) => props.theme.colors.primaryBlack};
  }

  .p-checkbox.p-component.p-disabled > .p-checkbox-box.p-component {
    opacity: 1;
    border-color: ${(props) => props.theme.colors.elementsDropDown};
  }

  .p-datatable-tbody > tr.p-highlight ${CellContainer} {
    background-color: transparent;
  }
`;

const DescriptionCell = styled(CellContainer)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export default ModuleGrid;
